import { useCallback, useEffect, useState } from 'react'
import { useEffectEvent } from 'use-effect-event'

import { useAuthenticatedUser } from '@/legacy-src/hooks/useAuthenticatedUser'

import { updateRecoveryKey } from '@/src/domains/ai/utils/recoveryKeyStorageUtils'
import { subscribeToLogoutEvent } from '@/src/domains/auth/utils/logoutEventUtils'

export const useAiSetupProviderRecoveryKey = () => {
  const { user } = useAuthenticatedUser()
  const [recoveryKey, setRecoveryKey] = useState<string | null>(null)

  const handleRecoveryKey = useEffectEvent((recoveryKey: string) => {
    updateRecoveryKey(user.id, recoveryKey)
  })

  useEffect(() => {
    if (!recoveryKey) {
      return
    }

    const controller = new AbortController()

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }

    const handlePageHide = () => {
      handleRecoveryKey(recoveryKey)
    }

    const handleLogout = () => {
      controller.abort()
      setRecoveryKey(null)
    }

    window.addEventListener('beforeunload', handleBeforeUnload, { signal: controller.signal })
    window.addEventListener('pagehide', handlePageHide, { signal: controller.signal })
    subscribeToLogoutEvent(handleLogout, { signal: controller.signal })

    return () => controller.abort()

    // effect events shouldn't be included in the dependency array, as they are guaranteed to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveryKey])

  const persistRecoveryKey = (heldRecoveryKey: string) => {
    updateRecoveryKey(user.id, heldRecoveryKey)
    setRecoveryKey(null)
  }

  const removeRecoveryKey = useCallback(() => {
    setRecoveryKey(null)
    updateRecoveryKey(user.id, null)
  }, [user.id])

  return { recoveryKey, holdRecoveryKey: setRecoveryKey, persistRecoveryKey, removeRecoveryKey }
}
